import React, { FC } from 'react';

import { Box, Tab, Tabs, Typography, styled } from '@mui/material';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import ConditionalLink from '~/components/ConditionalLink/ConditionalLink';
import CustomAccordion from '~/components/CustomAccordion/CustomAccordion';
import LoadingOverlay from '~/components/LoadingOverlay/LoadingOverlay';
import MuiTabPanel, { a11yProps } from '~/components/TabPanel/TabPanel';
import Avatar from '~/components/UI/Avatar';
import CardWrapper from '~/components/UI/CardWrapper/CardWrapper';
import VideoListItem from '~/components/VideoListItem/VideoListItem';
import useIsSsku from '~/hooks/tenant/useIsSsku';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import ExpertInfo from '~/pages/SingleVideoPage/components/Expert/ExpertInfo';
import { IVideoWithUrl } from '~/pages/SingleVideoPage/types';
import { buildExpertRoute } from '~/routes';
import { css } from '~/styled';
import {
  MIDDLE_MARGIN_PX,
  SKELETON_TEXT_HEIGHT_BIG_PX,
  SKELETON_TEXT_HEIGHT_PX,
  SMALL_MARGIN_PX,
} from '~/theme';
import { User } from '~/types';
import { secondsToHms } from '~/utils/dateUtils/secondsToHms';

interface ExpertProps {
  expert?: User | null;
  videos: IVideoWithUrl[];
  loading: boolean;
  restrictNavigation?: boolean;
  className?: string;
}

const loadingVideoList = new Array(3).fill(null);

const Expert: FC<ExpertProps> = ({
  className,
  expert,
  loading,
  videos,
  restrictNavigation = false,
}) => {
  const { t } = useTenantTranslation();
  const isSsku = useIsSsku();

  if (loading) {
    return (
      <Wrapper className={className}>
        <InfoWrapper>
          <Avatar size="lg" squared={isSsku} loading />
          <ExpertInfo loading />
        </InfoWrapper>
        <CardWrapper>
          <LoadingListTitle xs={SKELETON_TEXT_HEIGHT_PX} />
          <LoadingList>
            {loadingVideoList.map((item, index) => (
              <VideoListItem key={index} loading />
            ))}
          </LoadingList>
          <AnimatedSkeleton xs={SKELETON_TEXT_HEIGHT_BIG_PX} />
        </CardWrapper>
      </Wrapper>
    );
  }

  const expertLink = buildExpertRoute(expert?.slug);

  return (
    <Wrapper className={className} id="expertSection" data-testid="expertSection">
      <InfoWrapper>
        {expert?.avatar && (
          <ConditionalLink to={expertLink} withLink={!restrictNavigation}>
            <Avatar
              size="lg"
              src={expert.avatar}
              squared={isSsku}
              noBorder={isSsku}
              alt={expert.name}
            />
          </ConditionalLink>
        )}
        <StyledExpertInfo
          id={expert?.id}
          name={expert?.name}
          link={expertLink}
          bio={expert?.description}
          restrictNavigation={restrictNavigation}
        />
      </InfoWrapper>
      <CardWrapper id="moreFromExpert">
        <Tabs value={0}>
          <StyledTab label={t('page.video.moreFrom', { expert: expert?.name })} {...a11yProps(0)} />
        </Tabs>
        <TabPanel value={0} index={0}>
          {!loading && videos.length > 0 && (
            <div>
              <CustomAccordion itemsToShow={3} buttonCss={viewMoreBtnStyles}>
                {videos.map((video, index) => (
                  <VideoListItem
                    key={video.id}
                    title={video.title}
                    imageUrl={video.preview_url}
                    titleLink={restrictNavigation ? undefined : video.url}
                    totalDuration={secondsToHms(video.duration)}
                    withPlayIcon
                    wrapperStyles={index === videos.length - 1 ? listItemWrapperStyles : undefined}
                    data-testid="expertVideoItem"
                  />
                ))}
              </CustomAccordion>
            </div>
          )}
          {!loading && videos.length < 1 && (
            <Typography>{t('page.video.noVideosFrom', { expert: expert?.name })}</Typography>
          )}
          {loading && (
            <>
              <Typography>{t('common.loading')}...</Typography>
              <LoadingOverlay />
            </>
          )}
        </TabPanel>
      </CardWrapper>
    </Wrapper>
  );
};

export default Expert;

const listItemWrapperStyles = css`
  margin-bottom: 0 !important;
`;
const Wrapper = styled(Box)`
  width: 100%;
`;
const InfoWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 36px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 22px;
    flex-direction: row;
    align-items: flex-start;
  }
`;
const TabPanel = styled(MuiTabPanel)`
  margin-top: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-top: ${MIDDLE_MARGIN_PX};
  }
`;
const LoadingListTitle = styled(AnimatedSkeleton)`
  max-width: 250px;
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const LoadingList = styled('div')`
  margin-bottom: ${MIDDLE_MARGIN_PX};
`;
const StyledTab = styled(Tab, { shouldForwardProp: (prop) => prop !== 'selected' })<{
  selected?: boolean;
}>`
  max-width: unset;
  cursor: unset;

  &.Mui-selected {
    color: ${({ selected, theme }) => selected && theme.palette.common.white};
  }
`;
const viewMoreBtnStyles = css`
  margin-top: 23px;
`;
const StyledExpertInfo = styled(ExpertInfo)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: 36px;
  }
`;
